import React, { ChangeEvent, useCallback, useState } from 'react'
import { NotifiableError } from '@bugsnag/js'
import { verifyAccountExists } from '@/services/api/user'
import { formUtil, report } from '@/utils'
import { Button, ButtonBase, ErrorBox, Text } from '../common'
import { AuthModalView } from '../context/AuthContext/AuthContext'
import AuthInput from './AuthInput'

export interface Props {
  email: string
  setEmail: (email: string) => void
  setView: (view: AuthModalView) => void
}

const VerifyAccountForm: React.FC<Props> = ({ email, setEmail, setView }) => {
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()

      if (!formUtil.validateEmail(email)) {
        setError('Please enter a valid email address')
        setLoading(false)
        return
      }

      setError('')
      setLoading(true)

      try {
        const accountExists = await verifyAccountExists(email)

        // send to login if it does
        if (accountExists) {
          setView('login')
        } else {
          setView('register')
        }
      } catch (error: unknown) {
        report.error(error as NotifiableError)
        setError('An error occurred. Please try again later.')
      }
      setLoading(false)
    },
    [email, setView]
  )

  return (
    <>
      <Text as="p" preset="body.md" className="-mt-6 mb-4">
        Start by entering your email address to sign in or create your Angel
        account.
      </Text>
      <form className="" onSubmit={handleSubmit}>
        <ErrorBox errorText={error} className="mb-4" />
        <AuthInput
          name="email"
          type="email"
          placeholder="EMAIL"
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
          value={email}
        />
        <Button
          disabled={loading}
          className="mt-4 flex w-full items-center justify-center rounded-full"
        >
          <span>Continue</span>
        </Button>
      </form>

      <div className="mt-6 flex w-full flex-col items-center">
        <Text as="h4" preset="heading.sm">
          Already a member?
        </Text>
        <ButtonBase
          className="text-sm text-core-oxide"
          onClick={() => setView('login')}
          disabled={loading}
        >
          Login
        </ButtonBase>
      </div>
    </>
  )
}

export default VerifyAccountForm
